import {
  Button,
  Checkbox,
  DatePicker,
  DatePickerProps,
  Form,
  FormProps,
  Input,
  Radio,
  Select,
} from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import LinkIcon from "../../../assets/icons/LinkIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ZikZakTik from "../../../assets/icons/ZikZakTik";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  createWebinarAsync,
  singWebinarDataAsync,
  updateWebinarAsync,
} from "../../../store/features/EventManage/Webinars/webinarsApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { CreateWebinarData } from "../../../types/eventManage/webinars";
import { selectSearchOption, StringArrayOption } from "../../../utils/utils";
import CustomSection from "../../ui/CustomSection";
import { DimensionOfImage } from "../../ui/DimensionOfImage";
import FileUpload from "../../ui/FileUpload";
import IconButton from "../../ui/IconButton";
import IconicTitle from "../../ui/IconicTitle";

export default function AddWebinars() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [checkAll, setCheckAll] = useState(false);
  const [form] = Form.useForm();

  const { manageLoading, singleWebinarData } = useAppSelector(
    (state) => state.webinar
  );
  const { login } = useAppSelector((state) => state.login);

  const statusList = [
    "Published",
    "CSO-Admin-Approved",
    "Draft",
    "Pending",
    "Cancel",
  ];

  const filteredStatusList =
    login?.role_id?.role_type === "cso_admin"
      ? statusList.filter((item) => item !== "Published")
      : statusList.filter((item) => item !== "CSO-Admin-Approved");

  const createWebinarData = singleWebinarData as unknown as CreateWebinarData;

  const onFinish: FormProps<any>["onFinish"] = (values) => {
    const { webinarStartDate, webinarEndDate, ...rest } = values;
    const newValue = {
      ...rest,
      webinarStartDate: webinarStartDate ? webinarStartDate.valueOf() : null, // Convert to milliseconds
      webinarEndDate: webinarEndDate ? webinarEndDate.valueOf() : null, // Convert to milliseconds
    };

    if (id) {
      dispatch(
        updateWebinarAsync({
          data: newValue,
          others: {
            reRender,
            id: createWebinarData?._id,
          },
        })
      );
    } else {
      dispatch(
        createWebinarAsync({
          data: newValue,
          others: {
            reRender,
          },
        })
      );
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(
        singWebinarDataAsync({
          params: {
            id,
            reRender(val) {},
          },
        })
      );
    }
  }, [dispatch, id]);

  // add value in modal for view and edit
  useEffect(() => {
    if (id && createWebinarData?._id && manageLoading === false) {
      form.setFieldsValue({
        webinarId: createWebinarData?.webinarId,
        webinarTitle: createWebinarData?.webinarTitle,
        webinarEndDate: createWebinarData?.webinarEndDate
          ? moment(createWebinarData?.webinarEndDate)
          : null,
        webinarStartDate: createWebinarData?.webinarStartDate
          ? moment(createWebinarData?.webinarStartDate)
          : null,
        webinarOrganizedBy: createWebinarData?.webinarOrganizedBy,
        webinarHostBy: createWebinarData?.webinarHostBy,
        webinarVenueName: createWebinarData?.webinarVenueName,
        webinarVenueLinkStatus: createWebinarData?.webinarVenueLinkStatus,
        webinarStatus: createWebinarData?.webinarStatus,
        webinarRegistrationLink: createWebinarData?.webinarRegistrationLink,
        webinarVenueLink: createWebinarData?.webinarVenueLink,
        webinarGuests: createWebinarData?.webinarGuests?.map((guest) => ({
          fullName: guest?.fullName,
          profession: guest?.profession,
          designation: guest?.designation,
          photo: guest?.photo,
        })),
      });
    }
  }, [
    createWebinarData?._id,
    createWebinarData?.webinarEndDate,
    createWebinarData?.webinarGuests,
    createWebinarData?.webinarHostBy,
    createWebinarData?.webinarId,
    createWebinarData?.webinarOrganizedBy,
    createWebinarData?.webinarRegistrationLink,
    createWebinarData?.webinarStartDate,
    createWebinarData?.webinarStatus,
    createWebinarData?.webinarTitle,
    createWebinarData?.webinarVenueLink,
    createWebinarData?.webinarVenueLinkStatus,
    createWebinarData?.webinarVenueName,
    form,
    id,
    manageLoading,
  ]);

  function reRender(val: boolean) {
    if (val) {
      navigate("/admin/event-manage/webinars");
    }
  }

  const handleCheckAll = (e: CheckboxChangeEvent) => {
    setCheckAll(e.target.checked);
  };

  const handleDateChange: DatePickerProps["onChange"] = (
    date,
    dateString
  ) => {};

  return (
    <AdminLayout
      title={`${id ? "Webinar Update" : "Create a New Webinar"}`}
      backLink="/admin/event-manage/webinars"
    >
      <Form
        className="custom-form space-y-[14px]"
        layout="vertical"
        onFinish={onFinish}
        size="large"
        form={form}
      >
        <CustomSection>
          <div className="grid grid-cols-2 gap-x-2.5 w-full">
            <Form.Item
              name="webinarTitle"
              label="Webinar Title"
              rules={[
                {
                  required: true,
                  message: "Please select title",
                },
              ]}
            >
              <Input placeholder="Enter webinar title" size="large" />
            </Form.Item>

            <Form.Item
              name="webinarStartDate"
              label="Webinar Start Date & Time"
              rules={[
                {
                  required: true,
                  message: "Enter date and time",
                },
              ]}
            >
              <DatePicker
                format="DD MMM, YYYY hh:mm A"
                className="w-full"
                size="large"
                showTime={{
                  format: "hh:mm A",
                  use12Hours: true,
                }}
                onChange={(date) => handleDateChange(date, "webinarStartDate")}
                placeholder="DD MM YYYY hh:mm AM/PM"
              />
            </Form.Item>

            <Form.Item
              name="webinarEndDate"
              label="Webinar End Date & Time"
              rules={[
                {
                  required: true,
                  message: "Enter date and time",
                },
              ]}
            >
              <DatePicker
                format="DD MMM, YYYY hh:mm A"
                className="w-full"
                size="large"
                showTime={{
                  format: "hh:mm A",
                  use12Hours: true,
                }}
                onChange={(date) => handleDateChange(date, "webinarEndDate")}
                placeholder="DD MM YYYY hh:mm AM/PM"
              />
            </Form.Item>

            <Form.Item
              name="webinarOrganizedBy"
              label="Organized By"
              rules={[
                {
                  required: true,
                  message: "Please organization name",
                },
              ]}
            >
              <Input placeholder="Enter organization name" size="large" />
            </Form.Item>

            <Form.Item
              name="webinarHostBy"
              label="Host By"
              rules={[
                {
                  required: true,
                  message: "Please enter host name",
                },
              ]}
            >
              <Input placeholder="Enter host name" size="large" />
            </Form.Item>

            <Form.Item
              className="col-span-2"
              name="webinarVenueName"
              label="Webinar Venue Address"
              rules={[
                {
                  required: true,
                  message: "Please enter address",
                },
              ]}
            >
              <Input placeholder="Address" size="large" />
            </Form.Item>

            {/* <div className="flex items-center gap-x-2.5 mb-2.5 col-span-2">
              <Radio.Group
                name="webinarVenueLinkStatus"
                className="custom-radio"
                size="large"
              >
                {metingPlatform?.map((el, idx) => (
                  <Radio
                    className="text-body text-base font-medium leading-7 mr-3.5"
                    value={el}
                    key={idx}
                  >
                    {el}
                  </Radio>
                ))}
              </Radio.Group>
            </div> */}

            <Form.Item
              name="webinarVenueLink"
              label="Link"
              rules={[
                {
                  required: true,
                  message: "Please enter address",
                },
              ]}
            >
              <Input
                placeholder="https://meet.google.com/"
                size="large"
                prefix={<LinkIcon />}
              />
            </Form.Item>

            <Form.Item
              name="webinarVenueLinkStatus"
              label="Status"
              rules={[
                {
                  required: true,
                  message: "Please enter address",
                },
              ]}
            >
              <Select
                placeholder="Select"
                size="large"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={StringArrayOption(statusData)}
                suffixIcon={<SelectDownIcon />}
                disabled={!!id}
              />
            </Form.Item>
          </div>
        </CustomSection>

        <CustomSection>
          <IconicTitle
            icon={<ZikZakTik />}
            className="text-primary"
            title="Add webinar's Guests"
          />

          <Form.List initialValue={[undefined]} name="webinarGuests">
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map((field, index) => (
                    <div className="relative" key={field.key}>
                      <div className="grid grid-cols-2 gap-x-2.5 w-full mt-4">
                        <Form.Item
                          name={[index, "fullName"]}
                          label="Guest Full Name"
                          rules={[
                            {
                              required: true,
                              message: "Please enter name",
                            },
                          ]}
                        >
                          <Input placeholder="Enter full name" size="large" />
                        </Form.Item>

                        <Form.Item
                          name={[index, "profession"]}
                          label="Guest Profession"
                          rules={[
                            {
                              required: true,
                              message: "Please enter profession",
                            },
                          ]}
                        >
                          <Input
                            placeholder="e. g Senior Journalism"
                            size="large"
                          />
                        </Form.Item>

                        <Form.Item
                          name={[index, "designation"]}
                          label="Guest Designation"
                          rules={[
                            {
                              required: true,
                              message: "Please enter designation",
                            },
                          ]}
                        >
                          <Input placeholder="e. g Chief Guest" size="large" />
                        </Form.Item>

                        <Form.Item
                          name={[index, "photo"]}
                          label="Guest Photo"
                          valuePropName="fileUrl"
                          extra={DimensionOfImage({ width: 74, height: 93 })}
                          rules={[
                            {
                              required: false,
                              message: "Please upload photo",
                            },
                          ]}
                        >
                          <FileUpload
                            form={form}
                            accept={acceptFile}
                            name="photo"
                            onChange={(url) =>
                              form.setFieldsValue({ photo: url || undefined })
                            }
                          />
                        </Form.Item>
                      </div>

                      {fields.length > 1 ? (
                        <div className="border border-solid border-[#AFBACA] mt-1 mb-4 w-full" />
                      ) : null}

                      {fields.length > 1 ? (
                        <div className="flex justify-end mt-1 absolute right-0 -top-0">
                          <button
                            className="rounded-full bg-transparent border-none cursor-pointer"
                            onClick={() => remove(field.name)}
                          >
                            <span className="relative top-[1px]">
                              <DeleteIcon />
                            </span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}

                  <div
                    className={`flex items-end mb-3 ${
                      fields?.length % 2 === 0 ? "mt-6" : "mt-2"
                    }`}
                  >
                    <div className="flex justify-end ">
                      <IconButton
                        className="!text-sm !font-normal !rounded-lg !text-primary !bg-[#FFF5F4]"
                        type="text"
                        icon={<AddIcon color="#dc3545 " />}
                        onClick={() => add()}
                      >
                        <span>Add More</span>
                      </IconButton>
                    </div>
                  </div>
                </>
              );
            }}
          </Form.List>
        </CustomSection>

        <CustomSection>
          <div className="flex items-center gap-x-2.5 mb-4">
            <Form.Item
              name="webinarStatus"
              // label="Guest Designation"
              rules={[
                {
                  required: true,
                  message: "Please Select Status",
                },
              ]}
            >
              <Radio.Group
                className="custom-radio"
                // onChange={(e) => setOwnershipType(e.target.value)}
                //   value={ownershipType}
                size="large"
              >
                {filteredStatusList?.map((el, idx) => (
                  <Radio
                    className="text-body text-base font-medium leading-7 mr-3.5"
                    value={el}
                    key={idx}
                  >
                    {el}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </div>

          <div className="border border-solid border-[#d7dee8] mt-1 mb-4 w-full" />

          <div className="w-full justify-between items-center flex ">
            <Checkbox checked={checkAll} onChange={handleCheckAll}>
              <span className="text-body text-sm font-medium leading-normal">
                Yes! I confirmed and Publish this webinar
              </span>
            </Checkbox>

            <div className="justify-center items-center inline-flex gap-2 pt-6 ">
              <Button
                className="cancel-btn"
                htmlType="submit"
                loading={manageLoading}
              >
                Cancel
              </Button>
              <Button
                htmlType="submit"
                type="primary"
                loading={manageLoading}
                disabled={
                  !checkAll ||
                  form.getFieldsError().some(({ errors }) => errors.length > 0)
                }
              >
                {id ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </CustomSection>
      </Form>
    </AdminLayout>
  );
}

const metingPlatform = ["Google Meet", "Zoom", "Microsoft Teams"];
const statusData = ["Active", "Inactive"];
const acceptFile = ".jpg, .jpeg, .png";
